import React from "react";
import {
  Box,
  Typography,
  Grid,
  MenuItem,
  Chip,
  OutlinedInput,
  Select,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import FormTextField from "../../../../../../../core/ui/FormTextField";

const LEGAL_STATUS_OPTIONS = [
  "Individual",
  "Sole Proprietorship",
  "Corporation",
  "Partnership",
  "Others",
];

const PURPOSE_OPTIONS = [
  "ICPA Audit",
  "ICPA Payroll",
  "ICPA Tax",
  "ICPA Advisory",
  "ICPA Legal",
  "Bookkeeping",
  "Compilation",
];

const NATIONALITY_OPTIONS = ["Filipino", "American", "Japanese", "Others"];

const INDIVIDUAL_SOURCE_OPTIONS = [
  "Business",
  "Employment",
  "Investments",
  "Remittances",
  "Pensions",
  "Others",
];

const AVAILABLE_WIDTH = 307;
const estimateChipWidth = (label) => label.length * 7 + 28;

function getVisibleChips(selected) {
  let used = 0;
  const visible = [];
  for (let i = 0; i < selected.length; i++) {
    const w = estimateChipWidth(selected[i]);
    const overflowChipW = 36;
    if (i === selected.length - 1) {
      if (used + w <= AVAILABLE_WIDTH) {
        visible.push(selected[i]);
      } else {
        break;
      }
    } else {
      if (used + w + overflowChipW <= AVAILABLE_WIDTH) {
        visible.push(selected[i]);
        used += w + 4;
      } else {
        break;
      }
    }
  }
  return { visible, overflow: selected.length - visible.length };
}

const SectionBox = ({ title, children }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      p: 3,
      boxShadow: "0px 2px 4px rgba(0,0,0,0.08), 0px 0px 6px rgba(0,0,0,0.02)",
    }}
  >
    {title && (
      <Typography
        sx={{ fontSize: "16px", fontWeight: 600, color: "#292C2F", mb: 2 }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

export default function CompanyInfoStep({ form, handleChange, errors = {} }) {
  const handlePurposeChange = (event) => {
    const {
      target: { value },
    } = event;
    handleChange("purposeOfTransaction")({
      target: {
        value: typeof value === "string" ? value.split(",") : value,
      },
    });
  };

  const selected = form.purposeOfTransaction || [];
  const sourceOfFunds = form.individualSourceFunds || [];
  const sourceFundsApplicability =
    form.sourceFundsApplicability || "Applicable";
  const individualPEP = form.individualPEP || "No";

  const handleIndividualSourceToggle = (option) => (event) => {
    const checked = event.target.checked;
    const next = checked
      ? [...sourceOfFunds, option]
      : sourceOfFunds.filter((item) => item !== option);

    handleChange("individualSourceFunds")({
      target: {
        value: next,
      },
    });
  };

  const handleSourceFundsApplicabilityChange = (event) => {
    const value = event.target.value;
    handleChange("sourceFundsApplicability")({ target: { value } });

    if (value === "Not Applicable") {
      handleChange("individualSourceFunds")({ target: { value: [] } });
      handleChange("individualSourceOther")({ target: { value: "" } });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <SectionBox title="Company Info">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Grid container sx={{ gap: "24px" }}>
            <Grid item xs={12} sm="auto">
              <FormTextField
                label="Full Name Of Customer"
                required
                value={form.fullName || ""}
                onChange={handleChange("fullName")}
                placeholder="Nippon Express"
                maxwidth="361px"
                error={!!errors.fullName}
                errorMessage={errors.fullName}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <FormTextField
                label="Legal Status"
                required
                value={form.legalStatus || ""}
                onChange={handleChange("legalStatus")}
                select
                maxwidth="361px"
                error={!!errors.legalStatus}
                errorMessage={errors.legalStatus}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#999" }}>Select status...</span>
                      );
                    }
                    return selected;
                  },
                }}
              >
                {LEGAL_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>
          </Grid>
          <FormTextField
            label="Permanent Address"
            required
            value={form.permanentAddress || ""}
            onChange={handleChange("permanentAddress")}
            placeholder="HR 873 Capistrano st., Ibayo-tipas, taguig City"
            error={!!errors.permanentAddress}
            errorMessage={errors.permanentAddress}
          />
          <FormTextField
            label="Business Address"
            required
            value={form.businessAddress || ""}
            onChange={handleChange("businessAddress")}
            placeholder="HR 873 Capistrano st., Ibayo-tipas, taguig City"
            error={!!errors.businessAddress}
            errorMessage={errors.businessAddress}
          />
          <Grid container sx={{ gap: "24px" }}>
            <Grid item xs={12} sm="auto">
              <FormTextField
                label="SEC Registration"
                required
                value={form.secRegistration || ""}
                onChange={handleChange("secRegistration")}
                placeholder="123456789"
                maxwidth="361px"
                error={!!errors.secRegistration}
                errorMessage={errors.secRegistration}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#313131",
                  mb: 1,
                }}
              >
                Purpose & Nature of Transaction to be Undertaken{" "}
                <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <FormControl sx={{ width: "361px" }}>
                <Select
                  multiple
                  displayEmpty
                  value={selected}
                  onChange={handlePurposeChange}
                  input={
                    <OutlinedInput
                      sx={{
                        borderRadius: "6px",
                        fontSize: 14,
                        height: "40px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.purposeOfTransaction
                            ? "#d32f2f"
                            : "#D6D9DB",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#D6D9DB",
                          borderWidth: "1px",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#11783C",
                          borderWidth: "1px",
                        },
                        "& .MuiSelect-select": {
                          py: "8px",
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    />
                  }
                  renderValue={(sel) => {
                    if (sel.length === 0) {
                      return (
                        <Typography sx={{ color: "#999", fontSize: 14 }}>
                          Select purposes...
                        </Typography>
                      );
                    }
                    const { visible, overflow } = getVisibleChips(sel);
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          overflow: "hidden",
                        }}
                      >
                        {visible.map((v) => (
                          <Chip
                            key={v}
                            label={v}
                            size="small"
                            sx={{
                              bgcolor: "#f0f0f0",
                              fontSize: 12,
                              height: 22,
                              flexShrink: 0,
                            }}
                          />
                        ))}
                        {overflow > 0 && (
                          <Chip
                            label={`+${overflow}`}
                            size="small"
                            sx={{
                              bgcolor: "#003F2E",
                              color: "#fff",
                              fontSize: 11,
                              height: 22,
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 280 },
                    },
                  }}
                >
                  {PURPOSE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "3px",
                            border: "1.5px solid",
                            borderColor: selected.includes(opt)
                              ? "#003022"
                              : "#D6D9DB",
                            bgcolor: selected.includes(opt)
                              ? "#003022"
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {selected.includes(opt) && (
                            <svg
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 14 }}>{opt}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.purposeOfTransaction && (
                  <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: 0.5 }}>
                    {errors.purposeOfTransaction}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </SectionBox>

      {form.legalStatus === "Individual" && (
        <>
          <SectionBox title="In case of Individual">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Grid container sx={{ gap: "24px" }}>
                <Grid item xs={12} sm="auto">
                  <FormTextField
                    label="Nationality/Citizenship"
                    value={form.nationalityCitizenship || ""}
                    onChange={handleChange("nationalityCitizenship")}
                    select
                    maxwidth="361px"
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selectedValue) => {
                        if (!selectedValue) {
                          return <span style={{ color: "#999" }}>Select</span>;
                        }
                        return selectedValue;
                      },
                    }}
                  >
                    {NATIONALITY_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </FormTextField>
                </Grid>

                <Grid item xs={12} sm="auto">
                  <FormTextField
                    label="Occupation/Profession"
                    value={form.occupationProfession || ""}
                    onChange={handleChange("occupationProfession")}
                    maxwidth="361px"
                  />
                </Grid>

                <Grid item xs={12} sm="auto">
                  <FormTextField
                    label="Name of Establishment/Employer"
                    value={form.establishmentEmployer || "Not Applicable"}
                    onChange={handleChange("establishmentEmployer")}
                    disabled
                    maxwidth="361px"
                  />
                </Grid>
              </Grid>

              <FormTextField
                label="Current Address"
                required
                value={form.currentAddress || ""}
                onChange={handleChange("currentAddress")}
                placeHolder="Not Applicable"
              />

              <Grid container sx={{ gap: "24px" }}>
                <Grid item xs={12} sm="auto">
                  <FormTextField
                    label="Contact Number"
                    value={form.individualContactNumber || ""}
                    onChange={handleChange("individualContactNumber")}
                    placeHolder="Not Applicable"
                    maxwidth="361px"
                  />
                </Grid>

                <Grid item xs={12} sm="auto">
                  <FormTextField
                    label="Location of activity"
                    value={form.locationOfActivity || ""}
                    onChange={handleChange("locationOfActivity")}
                    placeHolder="Not Applicable"
                    maxwidth="361px"
                  />
                </Grid>
              </Grid>
            </Box>
          </SectionBox>

          <SectionBox>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#313131",
                mb: 0.5,
              }}
            >
              Source of Funds <span style={{ color: "#d32f2f" }}>*</span>
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#555", mb: 1.5 }}>
              You may choose more than one source:
            </Typography>

            <FormControl component="fieldset">
              <RadioGroup
                row
                value={sourceFundsApplicability}
                onChange={handleSourceFundsApplicabilityChange}
              >
                {[
                  { label: "Applicable", value: "Applicable" },
                  { label: "Not Applicable", value: "Not Applicable" },
                ].map((item) => (
                  <FormControlLabel
                    key={item.value}
                    value={item.value}
                    control={
                      <Radio
                        size="small"
                        sx={{
                          color: "#003022",
                          "&.Mui-checked": { color: "#003022" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 14 }}>
                        {item.label}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <Box sx={{ mt: 0.5 }}>
              {INDIVIDUAL_SOURCE_OPTIONS.map((option) => (
                <Box key={option}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sourceOfFunds.includes(option)}
                        onChange={handleIndividualSourceToggle(option)}
                        disabled={sourceFundsApplicability === "Not Applicable"}
                        sx={{
                          color: "#003022",
                          "&.Mui-checked": { color: "#003022" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 14 }}>{option}</Typography>
                    }
                  />
                </Box>
              ))}
            </Box>

            {sourceOfFunds.includes("Others") &&
              sourceFundsApplicability !== "Not Applicable" && (
                <Box sx={{ mt: 1, maxWidth: "100%" }}>
                  <FormTextField
                    value={form.individualSourceOther || ""}
                    onChange={handleChange("individualSourceOther")}
                    placeHolder="Please Specify"
                  />
                </Box>
              )}

            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#313131",
                  mb: 0.5,
                }}
              >
                Is the Individual a Politically Important Person (PEP) ?{" "}
                <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={individualPEP}
                  onChange={handleChange("individualPEP")}
                >
                  {[
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ].map((item) => (
                    <FormControlLabel
                      key={item.value}
                      value={item.value}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: "#003022",
                            "&.Mui-checked": { color: "#003022" },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 14 }}>
                          {item.label}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </SectionBox>
        </>
      )}
    </Box>
  );
}
